import { PendoReadinessEvents } from './pendo.machine';

type PendoLifecycleState = 'uninstalled' | 'stub_only' | 'agent_loaded_waiting_ready' | 'ready';
const PENDO_READINESS_POLL_INTERVAL_MS = 50;

export function createPendoReadinessMonitor(sendBack: (event: PendoReadinessEvents) => void, $window: Window) {
  // Declare before publishLifecycleState() so the ready callback closure always
  // has access to the assigned value, even if pendo.events.ready fires synchronously.
  // eslint-disable-next-line prefer-const
  let intervalId: number;
  let lastLifecycleState: PendoLifecycleState | null = null;
  let readyCallbackRegistered = false;

  const publishLifecycleState = () => {
    const lifecycleState = getPendoLifecycleState($window);

    if (
      lifecycleState === 'agent_loaded_waiting_ready' &&
      !readyCallbackRegistered &&
      typeof $window.pendo?.events?.ready === 'function'
    ) {
      readyCallbackRegistered = true;
      $window.pendo.events.ready(() => {
        if (lastLifecycleState !== 'ready') {
          lastLifecycleState = 'ready';
          $window.clearInterval(intervalId);
          sendBack({ type: 'PENDO_READY' });
        }
      });
    }

    if (lifecycleState !== lastLifecycleState && lastLifecycleState !== 'ready') {
      lastLifecycleState = lifecycleState;
      if (lifecycleState === 'ready') {
        $window.clearInterval(intervalId);
      }
      sendBack(getPendoLifecycleEvent(lifecycleState));
    }
  };

  publishLifecycleState();

  intervalId = $window.setInterval(publishLifecycleState, PENDO_READINESS_POLL_INTERVAL_MS);

  return () => {
    $window.clearInterval(intervalId);
  };
}

function isPendoReady($window: Window): boolean {
  return typeof $window.pendo?.isReady === 'function' && $window.pendo.isReady() === true;
}

function getPendoLifecycleState($window: Window): PendoLifecycleState {
  if ($window.pendo == null) {
    return 'uninstalled';
  }

  if (isPendoReady($window)) {
    return 'ready';
  }

  if (typeof $window.pendo.events?.ready === 'function') {
    return 'agent_loaded_waiting_ready';
  }

  return 'stub_only';
}

function getPendoLifecycleEvent(lifecycleState: PendoLifecycleState): PendoReadinessEvents {
  switch (lifecycleState) {
    case 'uninstalled':
      return { type: 'PENDO_NOT_INSTALLED' };
    case 'stub_only':
      return { type: 'PENDO_STUB_DETECTED' };
    case 'agent_loaded_waiting_ready':
      return { type: 'PENDO_AGENT_LOADED' };
    case 'ready':
      return { type: 'PENDO_READY' };
  }
}
